import { useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, Clock, PlayCircle } from "lucide-react";
import { useStartHereArticles } from "@/hooks/learn/useLearnArticles";
import { cn } from "@/lib/utils";

const MASCOT_SRC = "/genie-mascots/role/support.png";

const StartHereChecklist = () => {
  const navigate = useNavigate();
  const { data, isLoading } = useStartHereArticles();
  const articles = data?.articles ?? [];

  const completed = articles.filter((a) => !!a.completed_at).length;
  const total = articles.length;
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <div className="space-y-4">
      {/* Hero progress card */}
      <div className="relative overflow-hidden rounded-2xl border-2 border-genie-primary/20 bg-gradient-to-br from-genie-primary/5 via-white to-genie-secondary/5 p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <img
            src={MASCOT_SRC}
            alt="Help Genie mascot"
            className="w-14 h-14 sm:w-16 sm:h-16 object-contain flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <h2 className="text-lg sm:text-xl font-bold text-genie-text">
              Welcome to Help Genie
            </h2>
            <p className="text-xs sm:text-sm text-gray-600 mt-0.5 max-w-2xl">
              Short reads to go from new account to publishing a live Genie. Each takes 2–4 minutes.
            </p>
            {total > 0 && (
              <div className="mt-3 max-w-md">
                <div className="flex items-baseline justify-between mb-1.5">
                  <span className="text-[11px] font-semibold text-genie-text uppercase tracking-wide">
                    Your starter path
                  </span>
                  <span className="text-xs font-medium text-genie-primary">
                    {completed} of {total} complete · {percent}%
                  </span>
                </div>
                <div className="h-1.5 bg-genie-primary/10 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-genie-primary to-genie-secondary transition-all duration-500"
                    style={{ width: `${percent}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Step list */}
      {isLoading ? (
        <div className="grid gap-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-16 rounded-xl bg-white border border-gray-200 animate-pulse" />
          ))}
        </div>
      ) : (
        <ol className="space-y-2">
          {articles.map((article, i) => {
            const isComplete = !!article.completed_at;
            const stepNumber = String(i + 1).padStart(2, "0");
            return (
              <li key={article.id}>
                <button
                  onClick={() => navigate(`/learn/articles/${article.slug}`)}
                  className={cn(
                    "group w-full text-left flex items-stretch gap-0 rounded-xl border bg-white overflow-hidden transition-all duration-200",
                    isComplete
                      ? "border-genie-primary/40"
                      : "border-gray-200 hover:border-genie-primary/40 hover:shadow-md",
                  )}
                >
                  {/* Step number strip */}
                  <div
                    className={cn(
                      "flex flex-col items-center justify-center w-12 sm:w-14 flex-shrink-0 text-white font-bold transition-colors",
                      isComplete
                        ? "bg-gradient-to-br from-genie-primary to-genie-secondary"
                        : "bg-gradient-to-br from-genie-primary/80 to-genie-secondary/80 group-hover:from-genie-primary group-hover:to-genie-secondary",
                    )}
                  >
                    {isComplete ? (
                      <CheckCircle2 className="h-5 w-5" />
                    ) : (
                      <span className="text-base sm:text-lg">{stepNumber}</span>
                    )}
                  </div>

                  {/* Body */}
                  <div className="flex-1 min-w-0 px-4 py-2.5 sm:py-3 flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm sm:text-base font-semibold text-genie-text truncate">
                        {article.title}
                      </h3>
                      {article.summary && (
                        <p className="text-xs sm:text-sm text-gray-600 line-clamp-1 mt-0.5">{article.summary}</p>
                      )}
                      <div className="flex items-center gap-3 mt-1 text-[11px] text-gray-500">
                        <span className="inline-flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {article.est_minutes} min
                        </span>
                        {article.video_url && (
                          <span className="inline-flex items-center gap-1 text-genie-secondary">
                            <PlayCircle className="h-3 w-3" />
                            Video
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1 capitalize">
                          {article.category.replace(/-/g, " ")}
                        </span>
                      </div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-genie-primary transition-colors flex-shrink-0" />
                  </div>
                </button>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};

export default StartHereChecklist;
